import { LocationVarKey } from "@swivl-mono/types";
import { HelperText, Input, Label } from "@swivl-mono/ui";
import { useEffect, useState } from "react";

import { useLocationVariables } from "../../../../Hooks/useLocationVariables";
import LocationVarsModel from "../../../../Models/Variables/LocationVars.Model";
import { StepOutcome, WizardFooter, WizardStepShell } from "./WizardChrome";

interface GoogleStepProps {
  locationId: number;
  groupId: number;
  url: string;
  onUrl: (value: string) => void;
  onBack?: () => void;
  onClose: () => void;
  onNext: (outcome: StepOutcome) => void;
}

// The Google review link customers receive in review-request texts and emails.
export function GoogleStep({ locationId, groupId, url, onUrl, onBack, onClose, onNext }: GoogleStepProps): React.ReactElement {
  const [saving, setSaving] = useState(false);

  // Prefill from whatever's already set; never overwrite a live edit.
  const vars = useLocationVariables<LocationVarKey.CortexURLGoogleReview>([LocationVarKey.CortexURLGoogleReview], groupId, locationId);
  const existingUrl = vars?.[LocationVarKey.CortexURLGoogleReview];
  useEffect(() => {
    if (!url && existingUrl) onUrl(existingUrl);
  }, [existingUrl]);

  // Persists whatever's currently entered, independent of advancing/closing.
  const persist = async (): Promise<void> => {
    if (!url.trim()) return;
    setSaving(true);
    try {
      await LocationVarsModel.createVariable(LocationVarKey.CortexURLGoogleReview, url.trim(), undefined, locationId);
      await LocationVarsModel.createVariable(LocationVarKey.CortexURLGoogleReviewEnabled, "1", undefined, locationId);
      await LocationVarsModel.loadAllVariables(true);
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (): Promise<void> => {
    await persist();
    onNext(url.trim() ? "completed" : "skipped");
  };

  // "Save & close" must actually save the draft, not just close over it.
  const handleSaveAndClose = async (): Promise<void> => {
    await persist();
    onClose();
  };

  return (
    <WizardStepShell
      footer={
        <WizardFooter
          onBack={onBack}
          onClose={() => void handleSaveAndClose()}
          onSkip={() => onNext("skipped")}
          onApprove={() => void handleApprove()}
          saving={saving}
        />
      }
    >
      <div>
        {/* Input's own helperText renders below the box, not above it, so composed manually here. */}
        <Label className="mb-1 block">Google Review Link</Label>
        <HelperText className="mb-1">Where customers leave a Google review - included in review-request texts and emails.</HelperText>
        <Input value={url} onChange={(e) => onUrl(e.target.value)} placeholder="https://g.page/r/..." />
      </div>
    </WizardStepShell>
  );
}
